/**
 * Settings > Billing (BLU-1446, concept C "Bill first"): the org's own
 * PrintRun bill. Admins only (`org_settings.manage`, the whole admin strip in
 * Settings.tsx); hidden from pure-client orgs (`clientHidden`). One
 * self-contained tree, so the deferred Settings console can lift it as is.
 *
 * Branches: loading (a skeleton in the tab's real geometry), error (Retry,
 * never 0, L8), not set up (the locked empty state), active. `client` never
 * renders here (the tab is hidden), so it draws nothing.
 *
 * URL: `?month=<token>` picks the month (the period's UTC start day, a bare
 * token so a link survives; absent = the current month). Stripe's portal
 * returns to `?tab=billing&portal=done`: the tab then re-reads Stripe once
 * (`refresh: true`, which drops the backend's 10 minute cache) before the
 * normal reads run, and strips the param. Pay now does the same when the
 * customer comes back to this tab from Stripe's invoice page.
 *
 * Reads: `getOverview` and `listMonths` together (the backend shares one
 * subscription read and one invoice list between them), cached 5 minutes,
 * no refetch on focus (each is a Stripe call), one quiet retry at most.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { SectionError } from '@/components/shared/SectionError';
import { useOrganization } from '@/contexts/OrganizationContext';
import { updateSearchParams } from '@/lib/search-params';
import { useBillingFormat } from '../hooks/useBillingFormat';
import { useBillingPortal } from '../hooks/useBillingPortal';
import { billingErrorKind, billingQueryRetry } from '../lib/billing-errors';
import { parseUnitCents } from '../lib/billing-format';
import { CARD_CLASS } from '../lib/billing-ui';
import {
  buildMonthRail,
  findMonthEntry,
  type ActiveOverview,
  type BillingMonth,
} from '../lib/billing-view';
import { BillingBillCard } from './BillingBillCard';
import { BillingExplainer } from './BillingExplainer';
import { BillingMonthRail } from './BillingMonthRail';
import { BillingPiecesTable } from './BillingPiecesTable';
import { DeclinedNotice } from './DeclinedNotice';

const MONTH_PARAM = 'month';
const PORTAL_PARAM = 'portal';

const READ_OPTIONS = {
  staleTime: 5 * 60_000,
  refetchOnWindowFocus: false,
  retry: billingQueryRetry,
} as const;

/** The tab's two columns, shared by the page and its skeleton so nothing moves on arrival. */
const GRID_CLASS = 'grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)] lg:items-start';

export default function BillingTab() {
  const { t } = useTranslation('billing');
  const [searchParams, setSearchParams] = useSearchParams();
  const utils = trpc.useUtils();
  const portal = useBillingPortal();

  // ── Re-read Stripe (portal return, Pay now return) ─────────────────────────
  const [refreshing, setRefreshing] = useState(() => searchParams.get(PORTAL_PARAM) === 'done');
  const refreshFromStripe = useCallback(async () => {
    setRefreshing(true);
    try {
      // The vanilla client: a `{ refresh: true }` key would otherwise sit in
      // the query cache. The answer is set on the normal (no-input) key.
      const fresh = await utils.client.billing.getOverview.query({ refresh: true });
      utils.billing.getOverview.setData(undefined, fresh);
    } catch {
      // The plain read below runs next and shows its own error, with Retry.
    } finally {
      setRefreshing(false);
      void utils.billing.listMonths.invalidate();
    }
  }, [utils]);

  const portalReturnHandled = useRef(false);
  useEffect(() => {
    if (searchParams.get(PORTAL_PARAM) === null) return;
    setSearchParams((prev) => updateSearchParams(prev, { [PORTAL_PARAM]: null }), { replace: true });
    if (portalReturnHandled.current) return;
    portalReturnHandled.current = true;
    void refreshFromStripe();
  }, [searchParams, setSearchParams, refreshFromStripe]);

  // Pay now opens Stripe's invoice page in a new tab: re-read once when the
  // customer comes back to this one. One listener at a time, gone on unmount.
  const stopWatchingReturn = useRef<(() => void) | null>(null);
  const onPayNow = useCallback(() => {
    if (stopWatchingReturn.current) return;
    const onBack = () => {
      if (document.visibilityState !== 'visible') return;
      stopWatchingReturn.current?.();
      void refreshFromStripe();
    };
    // Registered after this click's own focus changes settle.
    const timer = window.setTimeout(() => {
      document.addEventListener('visibilitychange', onBack);
      window.addEventListener('focus', onBack);
    }, 0);
    stopWatchingReturn.current = () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onBack);
      window.removeEventListener('focus', onBack);
      stopWatchingReturn.current = null;
    };
  }, [refreshFromStripe]);
  useEffect(() => () => stopWatchingReturn.current?.(), []);

  // ── Reads ──────────────────────────────────────────────────────────────────
  const overview = trpc.billing.getOverview.useQuery(undefined, { ...READ_OPTIONS, enabled: !refreshing });
  const months = trpc.billing.listMonths.useQuery(undefined, { ...READ_OPTIONS, enabled: !refreshing });

  if (!overview.data) {
    if (overview.isError && !refreshing) {
      const stripeDown = billingErrorKind(overview.error) === 'stripe_unavailable';
      return (
        <div className="max-w-6xl">
          <SectionError
            title={stripeDown ? t('error.stripe.title') : t('error.title')}
            body={stripeDown ? t('error.stripe.body') : t('error.body')}
            onRetry={() => void overview.refetch()}
            retrying={overview.isFetching}
            retryLabel={t('retry')}
          />
        </div>
      );
    }
    return <BillingSkeleton />;
  }

  const data = overview.data;
  if (data.status !== 'active') {
    // A pure client never reaches this tab (it is hidden); draw nothing for one.
    if (data.status === 'client') return null;
    return (
      <div className={`max-w-6xl ${CARD_CLASS}`}>
        <EmptyState icon={Receipt} title={t('notSetUp.title')} description={t('notSetUp.description')} />
      </div>
    );
  }

  const monthsView: MonthsView = {
    list: months.data?.status === 'active' ? months.data.months : undefined,
    complete: months.data?.complete ?? false,
    state: months.isError && !months.data ? 'error' : months.data ? 'ready' : 'loading',
    settled: months.isSuccess,
    retry: () => void months.refetch(),
    retrying: months.isFetching,
  };

  return (
    <ActiveBilling
      overview={data}
      months={monthsView}
      portal={portal}
      onPayNow={onPayNow}
      searchParams={searchParams}
      setSearchParams={setSearchParams}
    />
  );
}

interface MonthsView {
  /** Undefined until read (or when the read failed). */
  list: BillingMonth[] | undefined;
  /** The list holds every invoice (so its oldest is the sign-up). */
  complete: boolean;
  state: 'loading' | 'error' | 'ready';
  /** The list has come back, so an unknown `?month=` really is unknown. */
  settled: boolean;
  retry: () => void;
  retrying: boolean;
}

interface ActiveBillingProps {
  overview: ActiveOverview;
  months: MonthsView;
  portal: ReturnType<typeof useBillingPortal>;
  onPayNow: () => void;
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
}

function ActiveBilling({ overview, months, portal, onPayNow, searchParams, setSearchParams }: ActiveBillingProps) {
  const fmt = useBillingFormat();
  const { organization } = useOrganization();
  const monthList = months.list;
  const monthsComplete = months.complete;
  const rail = useMemo(
    () => buildMonthRail(overview, monthList, monthsComplete),
    [overview, monthList, monthsComplete],
  );

  const token = searchParams.get(MONTH_PARAM);
  const found = findMonthEntry(rail, token);
  // A deep link to a past month waits for the month list; once it has come
  // back (or failed), an unknown month falls back to the current one.
  const waitingForMonth = found === null && months.state === 'loading';
  const entry = found ?? rail.current;

  useEffect(() => {
    if (!token || found || !months.settled) return;
    setSearchParams((prev) => updateSearchParams(prev, { [MONTH_PARAM]: null }), { replace: true });
  }, [token, found, months.settled, setSearchParams]);

  const currentToken = rail.current.token;
  const select = useCallback(
    (next: string) => {
      setSearchParams((prev) => updateSearchParams(prev, { [MONTH_PARAM]: next === currentToken ? null : next }));
    },
    [currentToken, setSearchParams],
  );

  const { includedPieces, extraPieceUnitAmountDecimal } = overview.plan;
  const extraPieceCents = parseUnitCents(extraPieceUnitAmountDecimal);
  const allowance =
    overview.hasPiecePrice && includedPieces !== null && extraPieceCents !== null
      ? { included: includedPieces, price: fmt.unitPrice(extraPieceCents, overview.currency) }
      : null;

  const piecesPeriod = entry.period;

  return (
    <div className="max-w-6xl space-y-4">
      {overview.failedInvoice && (
        <DeclinedNotice failed={overview.failedInvoice} portal={portal} onPayNow={onPayNow} />
      )}
      <div className={GRID_CLASS}>
        <BillingMonthRail
          rail={rail}
          activeToken={waitingForMonth ? '' : entry.token}
          onSelect={select}
          months={months}
        />
        <div className="min-w-0 space-y-4">
          {waitingForMonth ? (
            <BillSkeleton />
          ) : (
            <>
              <BillingBillCard overview={overview} entry={entry} portal={portal} onPayNow={onPayNow} />
              <BillingExplainer allowance={allowance} />
              {piecesPeriod && (
                <BillingPiecesTable
                  key={entry.token}
                  period={piecesPeriod}
                  isCurrent={entry.kind === 'current'}
                  knownCount={entry.pieces}
                  pendingCount={entry.kind === 'current' ? overview.pieces.pendingCount : 0}
                  ownOrgId={organization?.id ?? null}
                  ownName={organization?.name ?? ''}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** The bill card's shape: header, meter, four rows, total, card line. */
function BillSkeleton() {
  return (
    <div className={`${CARD_CLASS} space-y-0`} aria-hidden="true">
      <div className="space-y-2 border-b px-3 py-3 sm:px-4">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </div>
      <div className="space-y-2 border-b px-3 py-3 sm:px-4">
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-4 w-64 max-w-full" />
      </div>
      <div className="divide-y">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex h-12 items-center justify-between gap-4 px-3 sm:px-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-4 border-t px-3 py-3 sm:px-4">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-8 w-28" />
      </div>
    </div>
  );
}

function BillingSkeleton() {
  const { t } = useTranslation('billing');
  return (
    <div className="max-w-6xl" role="status" aria-busy="true">
      <span className="sr-only">{t('loading')}</span>
      <div className={GRID_CLASS}>
        <div className={CARD_CLASS} aria-hidden="true">
          <div className="border-b px-3 py-2.5">
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="space-y-1 p-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-1.5 px-2.5 py-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3.5 w-2/3" />
              </div>
            ))}
          </div>
        </div>
        <BillSkeleton />
      </div>
    </div>
  );
}
